import { connect } from 'react-redux';

import { fetchAllCarriersAction } from 'wanderu-ducks-carriers';
import { googleDataSelectors } from 'wanderu-ducks-google';
import {
    fetchDailyMinimumPricingAction,
    pricingSelectors,
} from 'wanderu-ducks-pricing';
import {
    fetchAllTripsAction,
    fetchCachedTripsAction,
    filterActions,
    filterSelectors,
    removeTripsWithId,
    searchActions,
    searchSelectors,
    tripSelectors,
} from 'wanderu-ducks-search';
import {
    i18nMoment,
    languageSelector,
    selectCurrency,
    selectLocale,
    localize,
} from 'wanderu-i18n';

import SearchContentContainer from './SearchContent';
import {
    DEPART_DIRECTION,
    RETURN_DIRECTION,
} from '../../constants';

const mapDispatchToProps = {
    clearSearchResults: searchActions.clearSearchResults,
    fetchAllTrips: fetchAllTripsAction.fetch,
    fetchDailyMinimumPricing: fetchDailyMinimumPricingAction.fetch,
    removeTrip: removeTripsWithId,
    resetAllFilters: filterActions.resetAllFilters,
    resetAllowNearby: filterActions.resetAllowNearby,
    resetArrivalStations: filterActions.resetArriveStations,
    resetArrivalTiming: filterActions.resetArrivalTiming,
    resetDepartureStations: filterActions.resetDepartStations,
    resetDepartureTiming: filterActions.resetDepartureTiming,
    resetDirectOnly: filterActions.resetDirectOnly,
    resetFeatures: filterActions.resetFeatures,
    resetFetchAllTrips: fetchAllTripsAction.reset,
    resetTravelModes: filterActions.resetTravelModes,
    setSortType: filterActions.setSortType,
    updateFilterParams: filterActions.updateFilterParams,
};

export function mapStateToProps(state, ownProps) {
    const { direction } = ownProps.match.params;
    const searchParams = searchSelectors.selectSearchParams(state, ownProps);
    const reduxDepartFilterParams = filterSelectors.selectFilterQueryParams(
        state,
        {
            ...searchParams,
            direction: DEPART_DIRECTION,
        },
    );
    const reduxReturnFilterParams = filterSelectors.selectFilterQueryParams(
        state,
        {
            ...searchParams,
            direction: RETURN_DIRECTION,
        },
    );

    const departTripId = searchSelectors.selectDepartTripId(state);
    const returnTripId = searchSelectors.selectReturnTripId(state);
    const {
        originPlaceId,
        destinationPlaceId,
        departDate,
        returnDate,
    } = searchParams;
    const locationData = googleDataSelectors.selectLatLongData(
        state,
        direction === DEPART_DIRECTION
            ? originPlaceId
            : destinationPlaceId,
    );
    const fromDate = direction === DEPART_DIRECTION
        ? departDate
        : returnDate;
    const searchAheadDate = i18nMoment(direction === DEPART_DIRECTION
        ? departDate
        : returnDate)
        .add(7, 'days')
        .format('YYYY-MM-DD');
    const dailyPricingKey = `${originPlaceId}-${destinationPlaceId}-${fromDate}-${searchAheadDate}`;

    const dailyDepartMinimumPricing = pricingSelectors.selectDailyDepartMinimumPricing(
        state,
        originPlaceId,
        destinationPlaceId,
    );
    const dailyReturnMinimumPricing = pricingSelectors.selectDailyReturnMinimumPricing(
        state,
        originPlaceId,
        destinationPlaceId,
    );
    const alternativeSearchDays = direction === DEPART_DIRECTION
        ? dailyDepartMinimumPricing
        : dailyReturnMinimumPricing;

    return {
        activeSortType: filterSelectors.selectActiveSortType(
            state,
            searchParams,
        ),
        alternativeSearchDays,
        batchId: searchSelectors.selectBatchId(state, searchParams),
        carriersFetching: fetchAllCarriersAction.selectWorking(state),
        currency: selectCurrency(state),
        dailyPricingError:
            fetchDailyMinimumPricingAction.selectError(state, dailyPricingKey),
        dailyPricingFetching:
            fetchDailyMinimumPricingAction.selectWorking(state, dailyPricingKey),
        dailyPricingSuccess:
            fetchDailyMinimumPricingAction.selectSuccess(state, dailyPricingKey),
        departCachedTripsSuccess: fetchCachedTripsAction.selectSuccess(state, {
            ...searchParams,
            direction: DEPART_DIRECTION,
        }),
        departTripId,
        departTripsError: fetchAllTripsAction.selectError(state, {
            ...searchParams,
            direction: DEPART_DIRECTION,
        }),
        departTripsExpireAt: fetchAllTripsAction.selectExpiresAt(state, {
            ...searchParams,
            direction: DEPART_DIRECTION,
        }),
        departTripsFetching: fetchAllTripsAction.selectWorking(state, {
            ...searchParams,
            direction: DEPART_DIRECTION,
        }),
        departTripsSuccess: fetchAllTripsAction.selectSuccess(state, {
            ...searchParams,
            direction: DEPART_DIRECTION,
        }),
        destinationCityName: searchSelectors.selectDestinationCityName(
            state,
        ),
        direction,
        isAllowNearbyFiltered: filterSelectors.selectAllowNearbyIsFiltered(
            state,
            searchParams,
        ),
        isArrivalStationsFiltered: filterSelectors.selectArriveStationsIsFiltered(
            state,
            searchParams,
        ),
        isArrivalTimingFiltered: filterSelectors.selectArrivalTimingIsFiltered(
            state,
            searchParams,
        ),
        isDepartureStationsFiltered: filterSelectors.selectDepartStationsIsFiltered(
            state,
            searchParams,
        ),
        isDepartureTimingFiltered: filterSelectors.selectDepartureTimingIsFiltered(
            state,
            searchParams,
        ),
        isDirectOnlyFiltered: filterSelectors.selectDirectOnlyIsFiltered(
            state,
            searchParams,
        ),
        isFeaturesFiltered: filterSelectors.selectFeaturesIsFiltered(
            state,
            searchParams,
        ),
        isTravelModesFiltered: filterSelectors.selectTravelModesIsFiltered(
            state,
            searchParams,
        ),
        language: languageSelector(state),
        locale: selectLocale(state),
        locationData,
        numberOfActiveFilters: filterSelectors.selectNumberOfActiveFilters(
            state,
            searchParams,
        ),
        originCityName: searchSelectors.selectOriginCityName(state),
        reduxDepartFilterParams,
        reduxReturnFilterParams,
        returnTripId,

        returnTripsError: fetchAllTripsAction.selectError(state, {
            ...searchParams,
            direction: RETURN_DIRECTION,
        }),
        returnTripsExpireAt: fetchAllTripsAction.selectExpiresAt(state, {
            ...searchParams,
            direction: RETURN_DIRECTION,
        }),
        returnTripsFetching: fetchAllTripsAction.selectWorking(state, {
            ...searchParams,
            direction: RETURN_DIRECTION,
        }),
        returnTripsSuccess: fetchAllTripsAction.selectSuccess(state, {
            ...searchParams,
            direction: RETURN_DIRECTION,
        }),
        searchAheadDate,
        searchParams,
        searchParamsAreValid: searchSelectors.selectSearchParamsAreValid(
            state,
            ownProps,
        ),
        sortOptions: filterSelectors.selectSortOptions(state, searchParams),
        totalTrips: tripSelectors.selectFilteredAndSortedTrips(state).length,
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(
    localize(SearchContentContainer, 'baseApp'),
);
